import { apiClient } from './api';

export type PurchaseOrderStatus = 'draft' | 'pending_approval' | 'approved' | 'sent' | 'partial' | 'received' | 'cancelled';

export interface PurchaseOrderItem {
  id: number;
  sku_id: number;
  sku_code: string;
  product_name: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  line_total: number;
}

export interface PurchaseOrder {
  id: number;
  po_number: string;
  supplier_id: number | null;
  supplier_name: string | null;
  status: PurchaseOrderStatus;
  order_date: string;
  expected_date: string | null;
  received_date: string | null;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  notes: string | null;
  created_by_user_id: number;
  items?: PurchaseOrderItem[];
  created_at: string;
  updated_at: string;
}

export interface Supplier {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
}

export interface ListPurchaseOrdersFilters {
  page?: number;
  limit?: number;
  status?: PurchaseOrderStatus;
  supplier_id?: number;
  search?: string;
}

export interface CreatePurchaseOrderInput {
  supplier_id?: number;
  expected_date?: string;
  notes?: string;
  items: Array<{ sku_id: number; quantity_ordered: number; unit_cost: number }>;
}

export interface PaginatedPurchaseOrders {
  data: PurchaseOrder[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export async function listPurchaseOrders(filters: ListPurchaseOrdersFilters = {}): Promise<PaginatedPurchaseOrders> {
  return apiClient.get<PaginatedPurchaseOrders>('/purchase-orders', filters as Record<string, any>);
}

export async function getPurchaseOrder(id: number | string): Promise<PurchaseOrder> {
  return apiClient.get<PurchaseOrder>(`/purchase-orders/${id}`);
}

export async function createPurchaseOrder(input: CreatePurchaseOrderInput): Promise<PurchaseOrder> {
  return apiClient.post<PurchaseOrder>('/purchase-orders', input);
}

export async function autoGeneratePurchaseOrder(threshold: number = 25): Promise<PurchaseOrder> {
  return apiClient.post<PurchaseOrder>('/purchase-orders/auto-generate', { threshold });
}

export async function updatePurchaseOrderStatus(id: number | string, status: PurchaseOrderStatus): Promise<PurchaseOrder> {
  return apiClient.patch<PurchaseOrder>(`/purchase-orders/${id}/status`, { status });
}

/**
 * VP-ADM-003 — Admin-only approval gate. A PO must be approved before it can
 * be sent to a supplier; the generic status endpoint rejects setting
 * status='approved' directly (400 USE_APPROVAL_ENDPOINT) and rejects
 * status='sent' unless the PO is already 'approved'.
 */
export async function approvePurchaseOrder(id: number | string): Promise<PurchaseOrder> {
  return apiClient.post<PurchaseOrder>(`/purchase-orders/${id}/approve`, {});
}

export interface ReceiveLineInput {
  po_item_id?: number;
  sku_id?: number;
  quantity: number;
  lot_number: string;
  expiration_date: string;
  location_code: string;
  /** Of `quantity`, how many arrived damaged (VP-WMS-006). Default 0. */
  damaged_quantity?: number;
}

/** Goods receipt: creates inventory lots, bumps received qty, recomputes PO status. */
export async function receivePurchaseOrder(id: number | string, lines: ReceiveLineInput[]): Promise<PurchaseOrder> {
  return apiClient.post<PurchaseOrder>(`/purchase-orders/${id}/receive`, { lines });
}

export interface ReceivingVariance {
  id: number;
  purchase_order_id: number;
  po_item_id: number;
  sku_code: string;
  quantity_expected: number;
  quantity_received: number;
  damaged_quantity: number;
  variance: number;
  reviewed: boolean;
  created_at: string;
}

/** VP-WMS-006 receiving-variance review queue (unreviewed by default). */
export async function listReceivingVariances(includeReviewed = false): Promise<ReceivingVariance[]> {
  const raw = await apiClient.get<any>('/purchase-orders/receiving-variances', { include_reviewed: includeReviewed });
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}

export async function listSuppliers(): Promise<Supplier[]> {
  const raw = await apiClient.get<{ data: Supplier[] }>('/purchase-orders/suppliers');
  return raw.data ?? (raw as any);
}

export async function createSupplier(name: string, contact?: { email?: string; phone?: string; address?: string; contact_name?: string }): Promise<{ id: number; name: string }> {
  return apiClient.post('/purchase-orders/suppliers', { name, ...contact });
}
